import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, lighten, useTheme } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { AgreementsByEntityChart, AgreementsByYearChart, STATUS_ORDER, useStatusRamp } from "./AgreementCharts";
import { AGREEMENT_STATUS_LABELS, formatMoney, formatMoneyCompact } from "./agreementLabels";

// Los contadores fallan por causas distintas a las del listado, así que llevan
// su propio mensaje: aquí un índice faltante no rompe una página, deja las
// cifras sin calcular.
const SUMMARY_ERROR_MESSAGE = {
  "failed-precondition":
    "Los totales necesitan un índice de Firestore que aún se está construyendo. Espera unos minutos y recarga.",
  "permission-denied":
    "No tienes permiso para consultar los totales de la cartera. Revisa las reglas de Firestore del proyecto.",
};

// Tarjeta de recuento con icono, al estilo del tablero: la cifra manda y el
// icono la acompaña. Cuando es accionable, toda la tarjeta filtra la tabla.
const CountTile = ({ count, label, icon, tone, loading, onClick, trailing }) => {
  const theme = useTheme();
  const color = tone ? theme.palette[tone].main : theme.palette.text.primary;
  const body = (
    // flexGrow en lugar de width: 100%. La app no monta CssBaseline, así que no
    // hay `box-sizing: border-box` global: con width: 100% el padding de
    // CardContent SUMA al ancho y la tarjeta se desborda justo esos 32px, que es
    // lo que empujaba el icono fuera del borde. Sólo se notaba en la tarjeta sin
    // onClick, porque las demás van dentro de un CardActionArea (display: flex)
    // y ahí CardContent es un hijo flex que encoge en vez de desbordarse.
    //
    // Centrado vertical: las tarjetas se estiran a la altura de la del saldo, y
    // sin esto el contenido se queda arriba dejando un hueco muerto debajo.
    <CardContent sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
        {/* minWidth: 0 es obligatorio. Un hijo flex no baja de su contenido por
            defecto (min-width: auto), así que una etiqueta larga empujaba el
            icono fuera de la tarjeta en vez de partirse en dos líneas. */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            {loading ? (
              <Skeleton width={40} height={40} />
            ) : (
              // Cifras proporcionales: a este tamaño los dígitos de ancho fijo
              // se ven sueltos. tabular-nums es para columnas, no para titulares.
              <Typography component="span" sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, color }}>
                {count}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          </Stack>
          {trailing}
        </Box>
        {icon && (
          <Box
            sx={{
              display: "flex",
              flexShrink: 0, // el icono nunca cede espacio: se recorta la etiqueta
              p: 1,
              borderRadius: "50%",
              color,
              bgcolor: alpha(color, 0.1),
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
    </CardContent>
  );

  // El Card es el contenedor flex. Sin esto, la tarjeta SIN onClick deja el
  // CardContent con la altura de su contenido —un bloque normal— y el centrado
  // vertical no tiene espacio donde centrar: se quedaba pegado arriba mientras
  // las demás sí se centraban, porque en ellas el CardActionArea (height: 100%)
  // ya estiraba el contenido.
  return (
    <Card variant="outlined" sx={{ height: "100%", display: "flex" }}>
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ display: "flex", alignItems: "stretch", flexGrow: 1 }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
};

export default function AgreementSummary({
  stats,
  expiry,
  loading,
  onSelectStatus,
  onSelectExpiry,
  // Umbral del aviso de presupuesto, de la configuración del módulo. Cae al 85%
  // mientras la configuración carga, para no pintar un aviso distinto por un
  // instante.
  warningPercentage = 85,
}) {
  const theme = useTheme();
  const ramp = useStatusRamp();
  const main = theme.palette.primary.main;

  const error = stats?.error || expiry?.error;
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        {SUMMARY_ERROR_MESSAGE[error] || "No se pudieron calcular los totales de la cartera."}
      </Alert>
    );
  }

  // Cartera anterior a los contadores: el documento aún no existe y hay que
  // sembrarlo. Se dice en vez de mostrar ceros, que se leerían como "no hay nada".
  if (stats?.missing) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Totales sin calcular</AlertTitle>
        Los convenios registrados antes de esta versión todavía no están contados. Un administrador
        debe ejecutar el recuento de la cartera para que el tablero muestre cifras.
      </Alert>
    );
  }

  const percentage = stats?.committedPercentage || 0;
  // El umbral del aviso lo fija la configuración del módulo; el 100% se marca
  // siempre como error, diga lo que diga ese número.
  const overCommitted = percentage >= 100;
  const nearLimit = percentage >= warningPercentage && !overCommitted;
  const meterColor = overCommitted
    ? theme.palette.error.main
    : nearLimit
      ? theme.palette.warning.main
      : main;

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
        <Grid item xs={12} sm={6} md={3}>
          <CountTile
            count={expiry?.active ?? 0}
            label="Convenios vigentes"
            icon={<EventAvailableOutlinedIcon />}
            tone="success"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CountTile
            count={expiry?.expired ?? 0}
            label="Convenios vencidos"
            icon={<EventBusyOutlinedIcon />}
            tone="error"
            loading={loading}
            onClick={() => onSelectExpiry?.("expired")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CountTile
            count={expiry?.soon ?? 0}
            label={`Próximos a vencer (${expiry?.soonDays ?? 30} días)`}
            icon={<HourglassBottomOutlinedIcon />}
            tone="warning"
            loading={loading}
            onClick={() => onSelectExpiry?.("soon")}
            trailing={
              !loading && expiry?.soon > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {formatMoney(expiry.soonValue)} en juego
                </Typography>
              ) : null
            }
          />
        </Grid>

        {/* Cifra principal del tablero: lo que aún se puede comprometer. */}
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ height: "100%", display: "flex" }}>
            <CardContent
              sx={{
                py: 2,
                flexGrow: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Saldo disponible
              </Typography>
              {loading ? (
                <Skeleton width="70%" height={44} />
              ) : (
                <Typography component="p" sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>
                  {formatMoneyCompact(stats?.remainingValue)}
                </Typography>
              )}
              {/* Medidor: una razón contra un límite. La pista es un paso más
                  claro de la misma rampa, para que el estado se lea en toda la
                  barra y no sólo en la parte llena. */}
              <Box sx={{ height: 8, borderRadius: 1, bgcolor: lighten(main, 0.85), mt: 1, overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.min(percentage, 100)}%`,
                    bgcolor: meterColor,
                    borderRadius: 1,
                    transition: "width .3s ease",
                  }}
                />
              </Box>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                {(overCommitted || nearLimit) && (
                  <ReportProblemOutlinedIcon
                    fontSize="inherit"
                    color={overCommitted ? "error" : "warning"}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {/* Redondeado: los decimales en una cifra de titular son ruido
                      (18,48% no dice más que 18%). El valor exacto está en la
                      ficha de cada convenio. */}
                  {Math.round(percentage)}% de {formatMoneyCompact(stats?.totalValue)} comprometido
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Aviso accionable: la cifra sola no dice qué hacer con ella. */}
      {!loading && expiry?.soon > 0 && (
        <Alert
          severity="warning"
          icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => onSelectExpiry?.("soon")}>
              Ver convenios
            </Button>
          }
        >
          Tienes {expiry.soon} {expiry.soon === 1 ? "convenio próximo" : "convenios próximos"} a
          vencerse en los próximos {expiry.soonDays} días.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <AgreementsByYearChart byEndYear={stats?.byEndYear} onSelectStatus={onSelectStatus} />
        </Grid>
        <Grid item xs={12} md={5}>
          <AgreementsByEntityChart byEntity={stats?.byEntity} entityNames={stats?.entityNames} />
        </Grid>

        {/* Composición por estado de toda la cartera. */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Convenios por estado</Typography>
              {(() => {
                const counts = stats?.byStatus || {};
                const total = STATUS_ORDER.reduce((acc, s) => acc + (counts[s] || 0), 0);
                if (loading) return <Skeleton variant="rounded" height={24} />;
                if (!total) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Sin convenios que mostrar.
                    </Typography>
                  );
                }
                return (
                  <>
                    <Box sx={{ display: "flex", height: 24, mt: 1 }}>
                      {STATUS_ORDER.map((status, i) => {
                        const value = counts[status] || 0;
                        if (!value) return null;
                        const isFirst = !STATUS_ORDER.slice(0, i).some((s) => counts[s]);
                        const isLast = !STATUS_ORDER.slice(i + 1).some((s) => counts[s]);
                        return (
                          <Box
                            key={status}
                            title={`${AGREEMENT_STATUS_LABELS[status]}: ${value}`}
                            onClick={() => onSelectStatus?.(status)}
                            sx={{
                              flexGrow: value,
                              flexBasis: 0,
                              bgcolor: ramp[status],
                              cursor: onSelectStatus ? "pointer" : "default",
                              mr: isLast ? 0 : "2px",
                              borderTopLeftRadius: isFirst ? 4 : 0,
                              borderBottomLeftRadius: isFirst ? 4 : 0,
                              borderTopRightRadius: isLast ? 4 : 0,
                              borderBottomRightRadius: isLast ? 4 : 0,
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Stack direction="row" flexWrap="wrap" sx={{ mt: 1.5, gap: 2 }}>
                      {STATUS_ORDER.map((status) => (
                        <Stack key={status} direction="row" alignItems="center" spacing={0.75}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: ramp[status], flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary">
                            {AGREEMENT_STATUS_LABELS[status]}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {counts[status] || 0}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
